import { addMinutes, endOfDay } from 'date-fns'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

import {
  DEFAULT_APP_TIMEZONE_PREFERENCE,
  type AppTimezonePreference,
  type TimeFormat,
} from './timezone.constants'
import {
  getAppTimezoneLabel,
  isValidSelectedTimezone,
  normalizeAppTimezonePreference,
  resolveTimezoneForDisplay,
} from './timezone.utils'

export type TimezoneDateInput = string | number | Date

export type TimezoneFormatOptions = {
  datePattern?: string
  includeTimezoneIndicator?: boolean
  timePattern?: string
  timeZone?: string
}

export type TimezoneUtcBoundary = {
  startUtcIso: string
  endUtcIso: string
}

export type TimezoneDatePeriod = {
  start: string
  end: string
}

export type DateRangeFilterValue = {
  startDate: string
  endDate: string
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const LOCAL_DATE_TIME_PARTS_LOCALE = 'en-US'

export function parseCalendarDatePart(datePart: string) {
  const [year, month, day] = datePart.split('-').map(Number)
  return { year, month, day }
}

export function getUtcDatePart(date: Date) {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-')
}

export function addCalendarDateDays(datePart: string, days: number) {
  const { year, month, day } = parseCalendarDatePart(datePart)
  return getUtcDatePart(new Date(Date.UTC(year, month - 1, day + days)))
}

export function getBrowserDatePart(date: Date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

export function getTimezoneEndOfToday(timeZone?: string) {
  const now = new Date()
  return endOfDay(timeZone && isValidSelectedTimezone(timeZone) ? utcToZonedTime(now, timeZone) : now)
}

export function addCalendarMonthsToDatePart(datePart: string, months: number) {
  const { year, month, day } = parseCalendarDatePart(datePart)
  return getUtcDatePart(new Date(Date.UTC(year, month - 1 + months, day)))
}

export function formatHourLabel(hour: number, timeFormat: TimeFormat) {
  const normalizedHour = Math.min(Math.max(Math.trunc(hour), 0), 23)
  if (timeFormat === '24h') return `${String(normalizedHour).padStart(2, '0')}:00`

  const period = normalizedHour < 12 ? 'AM' : 'PM'
  const displayHour = normalizedHour % 12 || 12
  return `${displayHour}:00 ${period}`
}

function getTimePattern(timeFormat: TimeFormat) {
  return timeFormat === '12h' ? 'h:mm a' : 'HH:mm'
}

function getFormatParts(preference: AppTimezonePreference, options: TimezoneFormatOptions) {
  const normalizedPreference = normalizeAppTimezonePreference(preference)
  const timeZone =
    options.timeZone && isValidSelectedTimezone(options.timeZone)
      ? options.timeZone
      : resolveTimezoneForDisplay(normalizedPreference)
  const timePattern = options.timePattern ?? getTimePattern(normalizedPreference.timeFormat)
  const datePattern = options.datePattern ?? 'MM/dd/yyyy'

  return { datePattern, timePattern, timeZone }
}

function getZonedDateInput(date: TimezoneDateInput, timeZone: string): Date {
  if (typeof date === 'string' && DATE_ONLY_PATTERN.test(date)) {
    return zonedTimeToUtc(`${date} 00:00:00`, timeZone)
  }

  return new Date(date)
}

function appendTimezoneIndicator(value: string, date: TimezoneDateInput, timeZone: string, include = false) {
  if (!include) return value
  return `${value} ${formatInTimeZone(getZonedDateInput(date, timeZone), timeZone, 'zzz')}`
}

export function formatTimezoneLocalDateTime(
  date: TimezoneDateInput,
  preference = DEFAULT_APP_TIMEZONE_PREFERENCE
) {
  const timeZone = resolveTimezoneForDisplay(normalizeAppTimezonePreference(preference))
  const formatter = new Intl.DateTimeFormat(LOCAL_DATE_TIME_PARTS_LOCALE, {
    day: '2-digit',
    fractionalSecondDigits: 3,
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone,
    year: 'numeric',
  })
  const parts = formatter.formatToParts(getZonedDateInput(date, timeZone))
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00'
  const hour = getPart('hour') === '24' ? '00' : getPart('hour')

  return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${hour}:${getPart('minute')}:${getPart('second')}.${getPart('fractionalSecond')}`
}

function getLocalDatePart(date: TimezoneDateInput, timeZone: string) {
  if (typeof date === 'string' && DATE_ONLY_PATTERN.test(date)) return date
  return formatInTimeZone(date, timeZone, 'yyyy-MM-dd')
}

function getUtcIsoForLocalWallTime(datePart: string, timePart: string, timeZone: string) {
  return zonedTimeToUtc(`${datePart} ${timePart}`, timeZone).toISOString()
}

function getNextCalendarDatePart(datePart: string) {
  return addCalendarDateDays(datePart, 1)
}

function getLocalHourBoundaryParts(datePart: string, hour: number) {
  if (hour < 24) return { boundaryDatePart: datePart, boundaryHour: hour }
  return {
    boundaryDatePart: getNextCalendarDatePart(datePart),
    boundaryHour: 0,
  }
}

function getUtcForLocalHourBoundary(datePart: string, hour: number, timeZone: string) {
  const { boundaryDatePart, boundaryHour } = getLocalHourBoundaryParts(datePart, hour)
  const boundaryKey = `${boundaryDatePart} ${String(boundaryHour).padStart(2, '0')}:00`
  let boundaryUtc = zonedTimeToUtc(`${boundaryKey}:00`, timeZone)

  for (let attempts = 0; attempts < 180; attempts += 1) {
    if (formatInTimeZone(boundaryUtc, timeZone, 'yyyy-MM-dd HH:mm') >= boundaryKey) return boundaryUtc
    boundaryUtc = addMinutes(boundaryUtc, 1)
  }

  return boundaryUtc
}

export function formatTimezoneDate(
  date: TimezoneDateInput,
  preference = DEFAULT_APP_TIMEZONE_PREFERENCE,
  options: Pick<TimezoneFormatOptions, 'datePattern' | 'includeTimezoneIndicator' | 'timeZone'> = {}
) {
  const { datePattern, timeZone } = getFormatParts(preference, options)
  const zonedDate = getZonedDateInput(date, timeZone)
  const formattedDate = formatInTimeZone(zonedDate, timeZone, datePattern)
  return appendTimezoneIndicator(formattedDate, zonedDate, timeZone, options.includeTimezoneIndicator)
}

export function formatTimezoneDateRangeLabel(
  period: TimezoneDatePeriod | null,
  preference = DEFAULT_APP_TIMEZONE_PREFERENCE
) {
  const timezoneLabel = getAppTimezoneLabel(preference)
  if (!period) return `Date ranges are interpreted in ${timezoneLabel}.`

  const startLabel = formatTimezoneDate(period.start, preference, {
    datePattern: 'MMM d, yyyy',
  })
  const endLabel = formatTimezoneDate(period.end, preference, {
    datePattern: 'MMM d, yyyy',
  })

  return `Date range: ${startLabel} - ${endLabel} in ${timezoneLabel}.`
}

export function formatTimezoneTime(
  date: TimezoneDateInput,
  preference = DEFAULT_APP_TIMEZONE_PREFERENCE,
  options: Pick<TimezoneFormatOptions, 'includeTimezoneIndicator' | 'timePattern' | 'timeZone'> = {}
) {
  const { timePattern, timeZone } = getFormatParts(preference, options)
  const zonedDate = getZonedDateInput(date, timeZone)
  const formattedTime = formatInTimeZone(zonedDate, timeZone, timePattern)
  return appendTimezoneIndicator(formattedTime, zonedDate, timeZone, options.includeTimezoneIndicator)
}

export function formatTimezoneHourLabel(
  hour: number,
  preference = DEFAULT_APP_TIMEZONE_PREFERENCE,
  options: { includeMinutes?: boolean } = {}
) {
  const normalizedPreference = normalizeAppTimezonePreference(preference)
  const normalizedHour = Math.min(Math.max(Math.trunc(hour), 0), 23)

  if (normalizedPreference.timeFormat === '24h') {
    return `${String(normalizedHour).padStart(2, '0')}:00`
  }

  const period = normalizedHour < 12 ? 'AM' : 'PM'
  const displayHour = normalizedHour % 12 || 12
  return options.includeMinutes ? `${displayHour}:00 ${period}` : `${displayHour} ${period}`
}

export function formatTimezoneDateTime(
  date: TimezoneDateInput,
  preference = DEFAULT_APP_TIMEZONE_PREFERENCE,
  options: TimezoneFormatOptions = {}
) {
  const { datePattern, timePattern, timeZone } = getFormatParts(preference, options)
  const zonedDate = getZonedDateInput(date, timeZone)
  const formattedDateTime = formatInTimeZone(zonedDate, timeZone, `${datePattern}, ${timePattern}`)
  return appendTimezoneIndicator(formattedDateTime, zonedDate, timeZone, options.includeTimezoneIndicator)
}

export function formatTimezoneHourRange(
  startDate: TimezoneDateInput,
  endDate: TimezoneDateInput,
  preference = DEFAULT_APP_TIMEZONE_PREFERENCE,
  options: Pick<TimezoneFormatOptions, 'includeTimezoneIndicator' | 'timePattern'> = {}
) {
  const { timePattern, timeZone } = getFormatParts(preference, options)
  const zonedStartDate = getZonedDateInput(startDate, timeZone)
  const zonedEndDate = getZonedDateInput(endDate, timeZone)
  const formattedRange = `${formatInTimeZone(zonedStartDate, timeZone, timePattern)} - ${formatInTimeZone(
    zonedEndDate,
    timeZone,
    timePattern
  )}`

  return appendTimezoneIndicator(formattedRange, zonedStartDate, timeZone, options.includeTimezoneIndicator)
}

export function getLocalDateUtcBoundaryIso(
  localDate: TimezoneDateInput,
  preference = DEFAULT_APP_TIMEZONE_PREFERENCE
): TimezoneUtcBoundary {
  const timeZone = resolveTimezoneForDisplay(normalizeAppTimezonePreference(preference))
  const datePart = getLocalDatePart(localDate, timeZone)
  const startUtcIso = getUtcIsoForLocalWallTime(datePart, '00:00:00', timeZone)
  const endDatePart = getNextCalendarDatePart(datePart)

  return {
    startUtcIso,
    endUtcIso: getUtcIsoForLocalWallTime(endDatePart, '00:00:00', timeZone),
  }
}

export function getInclusiveLocalDateUtcPeriodIso(
  startDatePart: string,
  endDatePart: string,
  preference = DEFAULT_APP_TIMEZONE_PREFERENCE
): TimezoneDatePeriod {
  const { startUtcIso } = getLocalDateUtcBoundaryIso(startDatePart, preference)
  const { endUtcIso } = getLocalDateUtcBoundaryIso(endDatePart, preference)

  return {
    start: startUtcIso,
    end: new Date(new Date(endUtcIso).getTime() - 1).toISOString(),
  }
}

export function getAppTimezoneDateRangeFilterValue(
  startDate: Date | null,
  endDate: Date | null,
  preference?: AppTimezonePreference
): DateRangeFilterValue {
  if (!preference) {
    return {
      startDate: startDate ? startDate.toISOString() : '',
      endDate: endDate ? endOfDay(endDate).toISOString() : '',
    }
  }

  const startDatePart = startDate ? getBrowserDatePart(startDate) : undefined
  const endDatePart = endDate ? getBrowserDatePart(endDate) : undefined

  const periodStartDatePart = startDatePart ?? endDatePart
  const periodEndDatePart = endDatePart ?? startDatePart

  if (!periodStartDatePart || !periodEndDatePart) return { startDate: '', endDate: '' }

  const period = getInclusiveLocalDateUtcPeriodIso(periodStartDatePart, periodEndDatePart, preference)

  return {
    startDate: startDatePart ? period.start : '',
    endDate: endDatePart ? period.end : '',
  }
}

export function getLocalHourUtcBoundaryIso(
  localDate: TimezoneDateInput,
  hour: number,
  preference = DEFAULT_APP_TIMEZONE_PREFERENCE
): TimezoneUtcBoundary {
  const timeZone = resolveTimezoneForDisplay(normalizeAppTimezonePreference(preference))
  const datePart = getLocalDatePart(localDate, timeZone)
  const normalizedHour = Math.min(Math.max(Math.trunc(hour), 0), 23)
  const startUtc = getUtcForLocalHourBoundary(datePart, normalizedHour, timeZone)
  const endUtc = getUtcForLocalHourBoundary(datePart, normalizedHour + 1, timeZone)

  return {
    startUtcIso: startUtc.toISOString(),
    endUtcIso: endUtc.toISOString(),
  }
}
